// ============================================================================
// DEEP DIVE VIEW — Stage 7 Operational Analytics v1
//
// Role access: ops_manager, tenant_admin
//
// Data: getDailyRollups(tenantId, buildDateRange(selectedDays)) — up to 90 reads.
// Invariant: Zero collection scans. Zero PHI reads.
//
// Layout:
//   1. Date range selector (7 / 14 / 30 / 60 / 90 days)
//   2. Summary cards (totals + computed rates)
//   3. Day-by-day table with TOTAL row
// ============================================================================

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../auth/AuthContext.jsx';
import {
  getDailyRollups, buildDateRange, safeNum, sumField,
} from '../../core/analyticsApi.js';
import {
  FileSearch, RefreshCw, AlertTriangle,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function shortDate(dateId) {
  const d = new Date(dateId + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function pct(num, denom) {
  if (!denom) return '—';
  return `${Math.round((num / denom) * 100)}%`;
}

function numOrDash(n) {
  return n > 0 ? n : '—';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, colorTw = 'text-white' }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/40 rounded-2xl p-4">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${colorTw}`}>{value ?? '—'}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function SkeletonBlock({ h = 'h-32' }) {
  return <div className={`bg-slate-800/30 border border-slate-700/20 rounded-2xl ${h} animate-pulse`} />;
}

// Table column definitions
const COLUMNS = [
  { key: 'date',     label: 'Date',       colorTw: 'text-slate-300' },
  { key: 'created',  label: 'Created',    colorTw: 'text-blue-400' },
  { key: 'needsInfo',label: 'Needs Info', colorTw: 'text-amber-400' },
  { key: 'ready',    label: 'Ready',      colorTw: 'text-indigo-400' },
  { key: 'sched',    label: 'Scheduled',  colorTw: 'text-purple-400' },
  { key: 'seen',     label: 'Seen',       colorTw: 'text-teal-400' },
  { key: 'closed',   label: 'Closed',     colorTw: 'text-emerald-400' },
  { key: 'rewReq',   label: 'Rew. Req',   colorTw: 'text-rose-400' },
  { key: 'rewRes',   label: 'Rew. Res',   colorTw: 'text-emerald-400' },
];

// ── Main View ─────────────────────────────────────────────────────────────────

const RANGE_OPTIONS = [
  { label: '7 days',  days: 7  },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
  { label: '60 days', days: 60 },
  { label: '90 days', days: 90 },
];

export default function DeepDiveView() {
  const { user } = useContext(AuthContext);
  const tenantId = user?.tenantId;

  const [days, setDays]           = useState(30);
  const [rollups, setRollups]     = useState(null);
  const [error, setError]         = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const dateIds = useMemo(() => buildDateRange(days), [days]);

  const load = async () => {
    // tenantId guaranteed by AnalyticsGuard (Stage 9)
    setRollups(null);
    try {
      const data = await getDailyRollups(tenantId, dateIds);
      setRollups(data);
      setError(null);
    } catch (err) {
      console.error('[DeepDiveView] Failed to load rollups:', err);
      setError('Failed to load analytics data. Please try again.');
    }
  };

  useEffect(() => { load(); }, [tenantId, days]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  // ── Per-row data ─────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    if (!rollups) return [];
    // Oldest → newest (buildDateRange already sorts oldest→newest)
    return rollups.map((r) => ({
      dateId:   r.dateId,
      created:  safeNum(r, 'flow', 'created'),
      needsInfo:safeNum(r, 'flow', 'needsInfo'),
      ready:    safeNum(r, 'flow', 'readyToSchedule'),
      sched:    safeNum(r, 'flow', 'scheduled'),
      seen:     safeNum(r, 'flow', 'seen'),
      closed:   safeNum(r, 'flow', 'closedLoop'),
      rewReq:   safeNum(r, 'rework', 'requestsCreated'),
      rewRes:   safeNum(r, 'rework', 'resolved'),
    }));
  }, [rollups]);

  // ── Totals row ───────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    if (!rollups) return null;
    return {
      created:   sumField(rollups, 'flow', 'created'),
      needsInfo: sumField(rollups, 'flow', 'needsInfo'),
      ready:     sumField(rollups, 'flow', 'readyToSchedule'),
      sched:     sumField(rollups, 'flow', 'scheduled'),
      seen:      sumField(rollups, 'flow', 'seen'),
      closed:    sumField(rollups, 'flow', 'closedLoop'),
      rewReq:    sumField(rollups, 'rework', 'requestsCreated'),
      rewRes:    sumField(rollups, 'rework', 'resolved'),
    };
  }, [rollups]);

  const loading = rollups === null;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
            <FileSearch className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Deep Dive</h1>
            <p className="text-xs text-slate-400">Day-by-day workflow analysis</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Range selector */}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-slate-800/60 border border-slate-700/40 rounded-xl px-3 py-2 text-xs
                       text-slate-200 focus:outline-none focus:border-violet-500/50"
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.days} value={o.days}>{o.label}</option>
            ))}
          </select>

          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60
                       text-slate-400 hover:text-white text-xs font-medium disabled:opacity-50 transition-all"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/30
                        rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* ── Summary Cards ─────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-medium text-slate-400 mb-3">
          Summary — Last {days} Days
        </h2>

        {loading ? (
          <SkeletonBlock h="h-24" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard
              label="Total Created"
              value={totals.created}
              colorTw="text-blue-400"
            />
            <SummaryCard
              label="Total Closed"
              value={totals.closed}
              colorTw="text-emerald-400"
            />
            <SummaryCard
              label="Completion Rate"
              value={pct(totals.closed, totals.created)}
              sub="closed / created"
              colorTw="text-cyan-400"
            />
            <SummaryCard
              label="Rework Rate"
              value={pct(totals.rewReq, totals.created)}
              sub={`${totals.rewReq} requests / ${totals.rewRes} resolved`}
              colorTw="text-rose-400"
            />
          </div>
        )}
      </section>

      {/* ── Day-by-Day Table ──────────────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-medium text-slate-400 mb-3">
          Day-by-Day Breakdown
        </h2>

        {loading ? (
          <SkeletonBlock h="h-64" />
        ) : rows.length === 0 ? (
          <div className="bg-slate-800/30 border border-slate-700/20 rounded-2xl p-8 text-center">
            <p className="text-slate-500 text-sm">No data for this period.</p>
          </div>
        ) : (
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/60">
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="px-3 py-2.5 text-left font-medium text-slate-400 whitespace-nowrap"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.dateId}
                      className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors"
                    >
                      <td className="px-3 py-2 text-slate-300 whitespace-nowrap font-medium">
                        {shortDate(r.dateId)}
                      </td>
                      <td className="px-3 py-2 text-blue-400">{numOrDash(r.created)}</td>
                      <td className="px-3 py-2 text-amber-400">{numOrDash(r.needsInfo)}</td>
                      <td className="px-3 py-2 text-indigo-400">{numOrDash(r.ready)}</td>
                      <td className="px-3 py-2 text-purple-400">{numOrDash(r.sched)}</td>
                      <td className="px-3 py-2 text-teal-400">{numOrDash(r.seen)}</td>
                      <td className="px-3 py-2 text-emerald-400">{numOrDash(r.closed)}</td>
                      <td className="px-3 py-2 text-rose-400">{numOrDash(r.rewReq)}</td>
                      <td className="px-3 py-2 text-emerald-400">{numOrDash(r.rewRes)}</td>
                    </tr>
                  ))}
                </tbody>
                {/* TOTAL row */}
                <tfoot>
                  <tr className="border-t-2 border-slate-600/60 bg-slate-800/60">
                    <td className="px-3 py-2.5 text-slate-300 font-semibold whitespace-nowrap">TOTAL</td>
                    <td className="px-3 py-2.5 text-blue-400 font-semibold">{totals.created}</td>
                    <td className="px-3 py-2.5 text-amber-400 font-semibold">{totals.needsInfo}</td>
                    <td className="px-3 py-2.5 text-indigo-400 font-semibold">{totals.ready}</td>
                    <td className="px-3 py-2.5 text-purple-400 font-semibold">{totals.sched}</td>
                    <td className="px-3 py-2.5 text-teal-400 font-semibold">{totals.seen}</td>
                    <td className="px-3 py-2.5 text-emerald-400 font-semibold">{totals.closed}</td>
                    <td className="px-3 py-2.5 text-rose-400 font-semibold">{totals.rewReq}</td>
                    <td className="px-3 py-2.5 text-emerald-400 font-semibold">{totals.rewRes}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </section>

      <p className="text-xs text-slate-600 text-center">
        Data sourced from pre-aggregated daily rollups · {days}-day window · Updates within seconds of each action
      </p>
    </div>
  );
}
